/**
 * F18 — Hook centrale per i tour onboarding v2.
 *
 * Responsabilità:
 *  - Lista tour visibili per il ruolo corrente (full + readonly)
 *  - Stato per tour (untouched / completed / dismissed) letto da `profiles`
 *  - Launch di un tour (driver.js + cleanup + persistenza completed)
 *  - Dismiss/reset di un tour (write su `dismissed_tours`/`completed_tours`)
 *  - Backward-compat v1: se `localStorage['dryconfig_tour_v1_completed']`,
 *    auto-migra a `completed_tours += ['welcome']` la prima volta
 *  - Mobile guard: tour disabilitato sotto 768px (banner toast)
 *  - Auto-launch welcome al primo login (untouched + role disponibile)
 *
 * Integrazione tour:
 *  - getSteps live importati lazy dai file `tours/<id>.ts`
 *  - Modalità readonly aggiunge banner via `TourViewerBanner`
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRoleGates } from '@/hooks/useRoleGates';
import {
  TOUR_META,
  TOUR_ORDER,
  type TourId,
  type TourMeta,
  type TourMode,
  type TourStatus,
  type TourStep,
  type OrgRole,
  getTourMode,
  getTourStatus,
} from './types';

/** Chiave legacy del tour v1 (localStorage). Migrata automaticamente in v2. */
const LEGACY_V1_KEY = 'dryconfig_tour_v1_completed';

/**
 * Fallback localStorage anti-replay per l'auto-launch del welcome.
 *
 * Scenario che la chiave previene: in dev (Vite HMR) o quando il DB è lento,
 * `profileState` arriva con `completed_tours=[]` momentaneamente → l'effetto
 * auto-launch considera welcome "untouched" e lo rilancia. Una volta visto
 * (autoLaunchedRef.current=true) la chiave viene scritta. I refresh successivi
 * la leggono per saltare l'auto-launch a prescindere dallo stato DB transitorio.
 * markCompleted continua comunque a scrivere su DB (source of truth).
 */
const WELCOME_AUTOLAUNCHED_KEY = 'dryconfig_tour_welcome_autolaunched';

/** Larghezza minima (px) per attivare i tour. Sotto: mostra toast "solo desktop". */
const MIN_DESKTOP_WIDTH = 768;

/**
 * Driver attivo a livello applicativo (singleton). Non vive dentro un ref del
 * componente perché altrimenti, quando il componente che ha avviato il tour
 * (es. TourSelector in /profile) viene smontato dalla navigate, il cleanup
 * useEffect distrugge il driver e il tour si chiude immediatamente.
 *
 * Lo state vive a module-scope: solo un tour alla volta in app, sempre.
 */
let _activeDriver: Driver | null = null;
let _activePollingInterval: number | null = null;
const _clearActivePolling = () => {
  if (_activePollingInterval !== null) {
    window.clearInterval(_activePollingInterval);
    _activePollingInterval = null;
  }
};

/** Dynamic import dei getSteps di ciascun tour (lazy). */
const STEP_LOADERS: Record<TourId, () => Promise<(mode: TourMode) => TourStep[]>> = {
  'welcome':              () => import('./tours/welcomeTour').then(m => m.getSteps),
  'configurator-wall':    () => import('./tours/configuratorWallTour').then(m => m.getSteps),
  'configurator-ceiling': () => import('./tours/configuratorCeilingTour').then(m => m.getSteps),
  'estimates':            () => import('./tours/estimatesTour').then(m => m.getSteps),
  'materials':            () => import('./tours/materialsTour').then(m => m.getSteps),
  'admin':                () => import('./tours/adminTour').then(m => m.getSteps),
};

interface ProfileTourState {
  onboarding_version: string;
  completed_tours: string[];
  dismissed_tours: string[];
}

export interface AvailableTour {
  meta: Omit<TourMeta, 'getSteps'>;
  mode: TourMode;
  status: TourStatus;
}

export function useTours() {
  const { user } = useAuth();
  const { role } = useRoleGates();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ===== Fetch profilo tour-state =====
  const { data: profileState } = useQuery({
    queryKey: ['profile-tour-state', user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<ProfileTourState | null> => {
      const { data, error } = await supabase
        .from('profiles')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .select('onboarding_version, completed_tours, dismissed_tours' as any)
        .eq('id', user!.id)
        .maybeSingle();
      if (error) {
        console.warn('[useTours] errore fetch profile tour state:', error);
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const d = (data ?? {}) as any;
      return {
        onboarding_version: d.onboarding_version ?? 'v2',
        completed_tours: Array.isArray(d.completed_tours) ? d.completed_tours : [],
        dismissed_tours: Array.isArray(d.dismissed_tours) ? d.dismissed_tours : [],
      };
    },
  });

  // ===== Backward-compat v1: migra localStorage → DB una volta sola =====
  const migrationDoneRef = useRef(false);
  useEffect(() => {
    if (migrationDoneRef.current) return;
    if (!user?.id || !profileState) return;
    const legacyDone = typeof window !== 'undefined' && localStorage.getItem(LEGACY_V1_KEY);
    if (!legacyDone) return;
    if (profileState.completed_tours.includes('welcome')) {
      // già migrato, ripulisci e amen
      localStorage.removeItem(LEGACY_V1_KEY);
      migrationDoneRef.current = true;
      return;
    }
    migrationDoneRef.current = true;
    void (async () => {
      const next = Array.from(new Set([...profileState.completed_tours, 'welcome']));
      const { error } = await supabase
        .from('profiles')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ completed_tours: next } as any)
        .eq('id', user.id);
      if (!error) {
        localStorage.removeItem(LEGACY_V1_KEY);
        queryClient.invalidateQueries({ queryKey: ['profile-tour-state', user.id] });
      }
    })();
  }, [user?.id, profileState, queryClient]);

  // ===== Tour disponibili per il ruolo corrente =====
  const availableTours: AvailableTour[] = useMemo(() => {
    if (!role) return [];
    const completed = profileState?.completed_tours ?? [];
    const dismissed = profileState?.dismissed_tours ?? [];
    return TOUR_ORDER.flatMap((id) => {
      const meta = TOUR_META[id];
      const mode = getTourMode(meta, role as OrgRole);
      if (!mode) return [];
      return [{
        meta,
        mode,
        status: getTourStatus(id, completed, dismissed),
      }];
    });
  }, [role, profileState]);

  // ===== Helper: device check =====
  const isMobile = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MIN_DESKTOP_WIDTH;
  }, []);

  // ===== Persistence helpers =====
  const markCompleted = useCallback(async (id: TourId) => {
    if (!user?.id) return;
    const current = profileState?.completed_tours ?? [];
    if (current.includes(id)) return;
    const next = Array.from(new Set([...current, id]));
    const { error } = await supabase
      .from('profiles')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ completed_tours: next } as any)
      .eq('id', user.id);
    if (error) {
      console.warn('[useTours] errore markCompleted:', error);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['profile-tour-state', user.id] });
  }, [user?.id, profileState, queryClient]);

  const dismissTour = useCallback(async (id: TourId) => {
    if (!user?.id) return;
    const current = profileState?.dismissed_tours ?? [];
    if (current.includes(id)) return;
    const next = Array.from(new Set([...current, id]));
    const { error } = await supabase
      .from('profiles')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ dismissed_tours: next } as any)
      .eq('id', user.id);
    if (error) {
      toast.error('Impossibile salvare la preferenza.');
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['profile-tour-state', user.id] });
  }, [user?.id, profileState, queryClient]);

  const resetTour = useCallback(async (id: TourId) => {
    if (!user?.id) return;
    const completed = (profileState?.completed_tours ?? []).filter(x => x !== id);
    const dismissed = (profileState?.dismissed_tours ?? []).filter(x => x !== id);
    const { error } = await supabase
      .from('profiles')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ completed_tours: completed, dismissed_tours: dismissed } as any)
      .eq('id', user.id);
    if (error) {
      toast.error('Impossibile resettare il tour.');
      return;
    }
    // Se l'utente resetta il welcome dal Profile, sblocca anche il flag locale
    // così l'auto-launch può ripartire al prossimo login.
    if (id === 'welcome' && typeof window !== 'undefined') {
      try { localStorage.removeItem(WELCOME_AUTOLAUNCHED_KEY); } catch { /* noop */ }
    }
    queryClient.invalidateQueries({ queryKey: ['profile-tour-state', user.id] });
  }, [user?.id, profileState, queryClient]);

  // ===== Run tour =====
  const startTour = useCallback(async (id: TourId) => {
    if (isMobile()) {
      toast.info('I tour guidati sono disponibili solo da desktop (≥768px).', {
        duration: 4000,
      });
      return;
    }
    if (!role) {
      toast.error('Impossibile avviare il tour: ruolo utente non rilevato.');
      return;
    }
    const meta = TOUR_META[id];
    const mode = getTourMode(meta, role as OrgRole);
    if (!mode) {
      toast.error('Tour non disponibile per il tuo ruolo.');
      return;
    }

    // Lazy import degli step (chunk per-tour, riduce bundle main)
    const getSteps = await STEP_LOADERS[id]();
    const steps = getSteps(mode);
    if (steps.length === 0) {
      toast.error('Tour vuoto (in costruzione).');
      return;
    }

    // Cleanup eventuale tour in corso (singleton module-level)
    if (_activeDriver) {
      _activeDriver.destroy();
      _activeDriver = null;
    }
    _clearActivePolling();

    // Mappa i TourStep a DriveStep di driver.js
    const driveSteps: DriveStep[] = steps.map((s) => ({
      element: s.element,
      popover: {
        title: s.title,
        description: mode === 'readonly' && s.descriptionReadonly
          ? s.descriptionReadonly
          : s.description,
        side: s.side ?? 'bottom',
        align: 'center',
      },
      disableActiveInteraction: s.disableInteraction ?? false,
    }));

    // Track polling interval cross-step, per cancellarlo se l'utente avanza
    // prima che il selettore compaia. Usa il singleton module-level.
    const clearPending = _clearActivePolling;

    /**
     * Quando uno step diventa attivo (onHighlightStarted), gestiamo:
     *  1. navigate verso step.navigate se siamo su altra rotta
     *  2. polling fino a quando document.querySelector(step.element) appare
     *  3. drv.refresh() per ridisegnare l'highlight sull'elemento appena montato
     *
     * driver.js, se l'elemento non esiste al momento dell'highlight, mostra il
     * popover al centro dello schermo o lo skippa. Senza wait dopo navigate il
     * tour di fatto si rompe sui multi-route step.
     */
    const handleStepActivated = (activeIndex: number) => {
      clearPending();
      const stepDef = steps[activeIndex];
      if (!stepDef) return;

      const targetPath = stepDef.navigate;
      const curPath = window.location.pathname + window.location.search;
      if (targetPath && curPath !== targetPath) {
        navigate(targetPath);
      }

      const sel = stepDef.element;
      if (!sel) return;
      // Already mounted → niente polling, driver.js highlight è OK
      if (document.querySelector(sel)) return;

      const timeoutMs = stepDef.waitForSelectorMs ?? 1500;
      const start = Date.now();
      _activePollingInterval = window.setInterval(() => {
        if (document.querySelector(sel)) {
          clearPending();
          // Force re-highlight: l'elemento esisteva null al primo render, ora c'è
          if (_activeDriver && _activeDriver.getActiveIndex() === activeIndex) {
            try {
              _activeDriver.refresh();
            } catch {
              /* noop: driver potrebbe essere stato distrutto */
            }
          }
        } else if (Date.now() - start > timeoutMs) {
          clearPending();
          console.warn(`[tour] selettore non trovato dopo ${timeoutMs}ms: ${sel}`);
        }
      }, 80);
    };

    const drv = driver({
      showProgress: true,
      progressText: 'Passo {{current}} di {{total}}',
      nextBtnText: 'Avanti →',
      prevBtnText: '← Indietro',
      doneBtnText: 'Fine',
      smoothScroll: true,
      allowClose: true,
      // overlayOpacity più scuro per attenzione + bordo arrotondato sull'highlight
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 8,
      steps: driveSteps,
      onDestroyed: () => {
        clearPending();
        _activeDriver = null;
      },
      onCloseClick: () => {
        // Close manuale = considerato completato (l'utente ha visto il tour)
        void markCompleted(id);
        drv.destroy();
      },
      onHighlightStarted: (_el, _step, opts) => {
        const idx = opts.state.activeIndex ?? 0;
        handleStepActivated(idx);
      },
    });

    _activeDriver = drv;
    drv.drive();

    // Mark completed quando il tour finisce (Done o Close).
    // Driver.js non ha un onComplete dedicato; ascoltiamo la nullificazione
    // di _activeDriver fatta da onDestroyed.
    const intervalId = window.setInterval(() => {
      if (!_activeDriver) {
        window.clearInterval(intervalId);
        void markCompleted(id);
      }
    }, 400);
  }, [isMobile, role, navigate, markCompleted]);

  // ===== Auto-launch welcome al primo login =====
  const autoLaunchedRef = useRef(false);
  useEffect(() => {
    if (autoLaunchedRef.current) return;
    if (!user?.id || !profileState || !role) return;
    if (isMobile()) return;
    // Fallback localStorage: se è stato auto-lanciato in passato, salta
    // (sopravvive a HMR e a race condition di profileState che ritorna
    // momentaneamente completed_tours=[]). Vedi commento WELCOME_AUTOLAUNCHED_KEY.
    if (typeof window !== 'undefined' && localStorage.getItem(WELCOME_AUTOLAUNCHED_KEY)) {
      autoLaunchedRef.current = true;
      return;
    }
    const welcomeStatus = getTourStatus(
      'welcome',
      profileState.completed_tours,
      profileState.dismissed_tours,
    );
    if (welcomeStatus !== 'untouched') {
      // Stato DB dice già visto → blinda il localStorage per i refresh futuri
      try { localStorage.setItem(WELCOME_AUTOLAUNCHED_KEY, '1'); } catch { /* noop */ }
      autoLaunchedRef.current = true;
      return;
    }
    autoLaunchedRef.current = true;
    try { localStorage.setItem(WELCOME_AUTOLAUNCHED_KEY, '1'); } catch { /* noop */ }
    // Delay 1.5s per dare tempo all'app di stabilizzarsi (sidebar montata, ecc.)
    const t = window.setTimeout(() => { void startTour('welcome'); }, 1500);
    return () => window.clearTimeout(t);
  }, [user?.id, profileState, role, startTour, isMobile]);

  // NB: no cleanup useEffect su unmount del driver. Il driver vive a
  // module-scope (singleton _activeDriver), così sopravvive allo smount del
  // componente che ha chiamato startTour (es. TourSelector in /profile, che
  // viene smontato dalla navigate del primo step → senza singleton, il driver
  // veniva distrutto immediatamente e il popover spariva subito).

  return {
    /** Tour visibili per l'utente corrente, con stato e modalità. */
    availableTours,
    /** Lancia un tour by id (rispetta mobile + role gating). */
    startTour,
    /** Marca tour come saltato (no auto-suggest in futuro). */
    dismissTour,
    /** Toglie da completed+dismissed: l'utente può rilanciarlo. */
    resetTour,
    /** Stato profilo grezzo (debug). */
    profileState,
    /** True se l'utente è su mobile (<768px). */
    isMobile: isMobile(),
  };
}

// Stub di lazy-loading per i tour non ancora implementati (placeholder fino a F18.4-F18.8).
// I file `tours/*.ts` esporteranno `export function getSteps(mode): TourStep[]`.
// Per evitare runtime error se importati prima della loro creazione,
// ogni file deve esistere con un export `getSteps`. Se manca, l'import fallisce
// e startTour mostra un toast "Tour vuoto (in costruzione)".
